import { inject } from '@angular/core';
import {
  CanActivateChildFn,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { ConnectivityService } from '@core/services/connectivity.service';
import { shouldUseOfflineFallback } from '@core/services/offline.utils';
import { SubscriptionAccessService } from '@core/services/subscription-access.service';

function resolveAccess(
  state: RouterStateSnapshot,
  router: Router,
  subscriptionAccessService: SubscriptionAccessService,
  connectivityService: ConnectivityService,
) {
  if (state.url.startsWith('/pricing') || state.url.startsWith('/my-account')) {
    return of(true);
  }

  return subscriptionAccessService.validateCurrentAccess().pipe(
    map((accessible) =>
      accessible
        ? true
        : router.createUrlTree(['pricing'], {
            queryParams: { from: state.url },
          }),
    ),
    catchError((error) => {
      if (shouldUseOfflineFallback(error) || !connectivityService.isOnline()) {
        return of(true);
      }

      return of(
        router.createUrlTree(['pricing'], {
          queryParams: { from: state.url },
        }),
      );
    }),
  );
}

export const subscriptionAccessGuard: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  const subscriptionAccessService = inject(SubscriptionAccessService);
  const connectivityService = inject(ConnectivityService);
  return resolveAccess(state, router, subscriptionAccessService, connectivityService);
};

export const subscriptionAccessChildGuard: CanActivateChildFn = (
  _childRoute,
  state,
) => {
  const router = inject(Router);
  const subscriptionAccessService = inject(SubscriptionAccessService);
  const connectivityService = inject(ConnectivityService);
  return resolveAccess(state, router, subscriptionAccessService, connectivityService);
};
